import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { DateTimePicker, DateTimePickerProps } from "@mui/x-date-pickers";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import { Box, Stack, Text } from "@chakra-ui/react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

dayjs.extend(utc);
dayjs.extend(timezone);

const DatepickerFormInput = <FormValues extends FieldValues = any>({
  timezone,
  label = "",
  name,
  deps = [],
  validate = () => true,
  validationFailMessage,
  dateTimeProps = {},
  transform = {
    input: (val: Date) => {
      return val ? dayjs.utc(val) : null;
    },
    output: (val: Dayjs) => {
      return val?.isValid() ? val?.toISOString() : null;
    },
  },
  method = "onAccept",
  skipValidate = false,
  overrideError = undefined,
}: {
  timezone: string;
  name: Path<FormValues>;
  deps?: string[];
  label?: string;
  validate?: (val: Dayjs, otherValues: FormValues) => boolean | string;
  skipValidate?: boolean;
  validationFailMessage?: string;
  dateTimeProps?: DateTimePickerProps<Dayjs>;
  transform?: { input: (val: any) => any; output: (val: any) => any };
  method?: "onAccept" | "onChange";
  overrideError?: string;
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();

  const props = {
    [method]: (field) => ({
      [method]: (date) => field.onChange(transform.output(date)),
    }),
    // Produces output like onAccept={field.onChange()} or onChange={field.onChange}
  };

  const validationProps = skipValidate
    ? {}
    : {
        validate: (fieldToValidate, otherValues) => {
          try {
            if (fieldToValidate === null || fieldToValidate === undefined) {
              return `${name} is required.`;
            }

            const validationResult = validate(
              transform.input(fieldToValidate),
              otherValues
            );
            if (!validationResult) {
              return validationFailMessage || `Failed validating ${name}`;
            }

            return true;
          } catch (e) {
            return validationFailMessage || `Failed validating ${name}`;
          }
        },
      };

  return (
    <Stack w="full" spacing={0}>
      <Text fontWeight={"medium"}>{label}</Text>
      <Controller<FormValues>
        name={name}
        control={control}
        rules={{
          deps,
          ...validationProps,
        }}
        render={({ field }) => {
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                {...field}
                value={transform.input(field.value)}
                onChange={(date) => {
                  return field.onChange(transform.output(date));
                }}
                {...props[method](field)}
                timezone={timezone}
                closeOnSelect
                {...dateTimeProps}
              />
            </LocalizationProvider>
          );
        }}
      />
      <Box h={4}>
        {overrideError ? (
          <Text fontSize={"xs"} color="red.300" lineHeight={4}>
            {overrideError}
          </Text>
        ) : errors[name] ? (
          <Text fontSize={"xs"} color="red.300" lineHeight={4}>
            {`${errors[name].message}`}
          </Text>
        ) : null}
      </Box>
    </Stack>
  );
};

export default DatepickerFormInput;
